import { Link } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import SeoHead from '@/components/seo/SeoHead';

const STEPS = [
  {
    step: '01',
    title: 'Consent capture',
    description:
      'Before the session begins, the clinician records client consent within the platform. Consent is logged with a timestamp and linked to the session record. No session proceeds without it.',
  },
  {
    step: '02',
    title: 'Secure transcription',
    description:
      'Session audio is transcribed in real time. Audio is processed in-memory only and is never stored persistently. The transcript remains within the tenant-scoped session record, accessible only to authorised clinicians.',
  },
  {
    step: '03',
    title: 'AI-assisted draft generation — including the triage note',
    description:
      'The platform generates two key outputs from the session transcript: a triage note summarising presenting issues, risk indicators, and provisional referral recommendations; and a full structured IAR-DST draft covering all assessment domains. Both are drafts only — the clinician has not yet reviewed them.',
  },
  {
    step: '04',
    title: 'Clinician review and edit',
    description:
      'The clinician reviews both the triage note and the full IAR draft in detail. Every section is editable. The clinician adds their clinical observations, adjusts the risk framing, and ensures the content accurately reflects the session. The AI output is a time-saving starting point — clinical authorship and responsibility remain entirely with the clinician.',
  },
  {
    step: '05',
    title: 'Approve or reject',
    description:
      'After review, the clinician approves or rejects the draft. Rejected drafts are not exported and are flagged in the audit trail with a reason. Approved drafts become available for export. Every approval and rejection is timestamped and attributed to the reviewing clinician.',
  },
  {
    step: '06',
    title: 'Export to support compliance and reporting',
    description:
      'Approved drafts are exported in your choice of format—PDF (watermarked official record), Word/DOCX (editable working copy), JSON (structured data bundle), ZIP Bundle (complete record with PDF, JSON, and metadata manifest), Markdown, or plain text (clipboard with provenance footer). Each export event is recorded in the audit trail with clinician identity, timestamp, format, and custody transfer acknowledgement.',
  },
];

const GUARDRAILS = [
  'AI output is draft-only — never auto-submitted or treated as a final clinical record',
  'Clinician review and approval is mandatory before any export',
  'Audio is never stored persistently — processed in-memory only',
  'Every action is timestamped, attributed, and logged in the audit trail',
  'Consent is recorded and linked to each session before any workflow begins',
  'Data is tenant-scoped — no cross-client or cross-service data leakage',
  'Rejected drafts are retained in the audit trail but never exported',
  'All exports are logged with clinician identity and timestamp',
];

const EXPORTS_NOW = [
  { label: 'PDF (Official Record)', detail: 'Watermarked PDF/A with custody transfer statement on every page — the legal export record' },
  { label: 'Word / DOCX (Working Copy)', detail: 'Editable Word document marked "Not Authoritative" — for internal review or clinical discussion' },
  { label: 'JSON (Reference Bundle)', detail: 'Canonical structured JSON for integration with EMR/CRM systems and clinical data pipelines' },
  { label: 'ZIP Bundle (Complete Record)', detail: 'Official PDF + canonical JSON + metadata manifest in a single file — the complete portable clinical record' },
  { label: 'Markdown (Clipboard)', detail: 'Formatted Markdown with provenance footer — paste directly into clinical tools that support rich text' },
  { label: 'Plain Text (Clipboard)', detail: 'Clean plain text with provenance footer — paste into any system, email, or messaging tool' },
  { label: 'Audit trail export', detail: 'Complete action log for compliance and service reporting' },
  { label: 'Session metadata', detail: 'Consent record, timestamps, clinician attribution' },
];

const EXPORTS_COMING = [
  'Clinical record system integration (HL7 / FHIR R4)',
  'MHTP-aligned documentation export',
  'Bulk reporting export for service-level compliance',
  'Referral letter generation support',
];

const WEBPAGE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'IAR-DST Documentation Software | AI-Assisted IAR Notes | Notematic',
  description:
    'Notematic accelerates IAR-DST documentation in Medicare Mental Health Centres. AI-assisted drafts, triage notes, clinician review controls, governance, and a full audit trail.',
  url: 'https://www.notematic.com.au/workflows/iar',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.notematic.com.au/' },
      { '@type': 'ListItem', position: 2, name: 'Workflows', item: 'https://www.notematic.com.au/workflows/iar' },
      { '@type': 'ListItem', position: 3, name: 'IAR-DST Workflow', item: 'https://www.notematic.com.au/workflows/iar' },
    ],
  },
};

export default function IarWorkflowPage() {
  return (
    <PageLayout>
      <SeoHead
        title="IAR-DST Documentation Software | AI-Assisted IAR Notes | Notematic"
        description="Notematic accelerates IAR-DST documentation in Medicare Mental Health Centres. AI-assisted drafts, triage notes, clinician review controls, and a complete audit trail. Not a replacement — an accelerator."
        canonical="/workflows/iar"
        jsonLd={WEBPAGE_JSON_LD}
      />
      {/* Hero */}
      <section className="section-padding bg-gradient-to-b from-white to-secondary-50 dark:from-secondary-900 dark:to-secondary-800">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-50 dark:bg-primary-950 text-primary-700 dark:text-primary-300 text-sm font-medium mb-8">
            Workflows — IAR-DST Framework
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 dark:text-white mb-6">
            Accelerating note-taking in the{' '}
            <span className="text-gradient">IAR-DST workflow</span>
          </h1>
          <p className="text-lg text-secondary-600 dark:text-secondary-300 mb-10">
            Notematic does not replace the Initial Assessment and Referral Decision Support Tool
            (IAR-DST) process. It accelerates the documentation burden within it — so clinicians
            spend less time writing and more time with clients. The IAR-DST workflow remains yours.
            We just help you get the notes done faster, with a complete audit trail built in.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn btn-primary btn-lg">Book Demo</Link>
            <Link to="/solutions/mmhc" className="btn btn-outline btn-lg">See MMHC Solutions</Link>
          </div>
        </div>
      </section>

      {/* What is IAR-DST */}
      <section className="section-padding bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-3xl mx-auto">
          <div className="rounded-xl bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 p-8">
            <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-3">
              What is the IAR-DST?
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400 text-sm leading-relaxed">
              The Initial Assessment and Referral Decision Support Tool (IAR-DST) is an
              evidence-based clinical framework used to determine the appropriate level of
              mental health care for a client. It is required under Medicare Better Access
              guidelines for clients accessing a Mental Health Treatment Plan, and covers
              multiple clinical domains including presenting issues, mental state, risk,
              functioning, and referral recommendations. Clinicians trained in IAR-DST
              already know how to conduct the assessment — Notematic supports the
              documentation of it, not the clinical judgement within it.
            </p>
          </div>
        </div>
      </section>

      {/* Acceleration not replacement */}
      <section className="section-padding bg-secondary-50 dark:bg-secondary-900">
        <div className="container-custom max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-3">
              Accelerating your workflow — not replacing it
            </h2>
            <p className="text-secondary-500 dark:text-secondary-400 text-sm max-w-xl mx-auto">
              Clinicians conduct the IAR assessment exactly as they always have. Notematic handles
              what comes after — turning the session into structured, reviewable documentation.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: 'You still conduct the assessment', detail: 'The clinical judgement, the therapeutic relationship, and the IAR-DST framework remain entirely yours.' },
              { label: 'We eliminate the write-up burden', detail: 'Notematic generates a structured draft from the session transcript so you start with something, not a blank page.' },
              { label: 'Triage note included', detail: 'A triage note summarising presenting risk, issues, and referral level is generated alongside the full IAR draft.' },
              { label: 'You are always the author', detail: 'You review, edit, and approve every word before it leaves the system. Clinical responsibility remains with you.' },
            ].map((item) => (
              <div key={item.label} className="rounded-xl bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 p-5">
                <div className="font-semibold text-secondary-900 dark:text-white text-sm mb-1">{item.label}</div>
                <div className="text-xs text-secondary-500 dark:text-secondary-400 leading-relaxed">{item.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step-by-step workflow */}
      <section className="section-padding bg-secondary-50 dark:bg-secondary-900">
        <div className="container-custom max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-4">
              How the workflow runs
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400">
              Every session follows a fixed, clinician-controlled process. No step is
              skipped or automated without clinician involvement.
            </p>
          </div>
          <div className="space-y-8">
            {STEPS.map((s) => (
              <div key={s.step} className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-sm">
                  {s.step}
                </div>
                <div className="pt-1">
                  <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">{s.title}</h3>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400 leading-relaxed">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance and compliance */}
      <section className="section-padding bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-3">
              Governance and compliance built in — not bolted on
            </h2>
            <p className="text-secondary-500 dark:text-secondary-400 text-sm max-w-xl mx-auto">
              These are not optional settings. They are structural constraints built into every session.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {GUARDRAILS.map((g) => (
              <div key={g} className="flex items-start gap-3 rounded-lg bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 p-4">
                <svg className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-secondary-700 dark:text-secondary-300">{g}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Exports */}
      <section className="section-padding bg-secondary-50 dark:bg-secondary-900">
        <div className="container-custom max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-3">
              Exports — available now and what's coming
            </h2>
            <p className="text-secondary-500 dark:text-secondary-400 text-sm max-w-xl mx-auto">
              Every approved export is logged with clinician identity and timestamp. Nothing leaves the system without a review trail.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-xl bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 p-6">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300 text-xs font-semibold mb-4">
                Available now
              </div>
              <ul className="space-y-4">
                {EXPORTS_NOW.map((e) => (
                  <li key={e.label}>
                    <div className="text-sm font-semibold text-secondary-900 dark:text-white">{e.label}</div>
                    <div className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">{e.detail}</div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 p-6">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-400 text-xs font-semibold mb-4">
                On the roadmap
              </div>
              <ul className="space-y-3">
                {EXPORTS_COMING.map((e) => (
                  <li key={e} className="flex items-start gap-2 text-sm text-secondary-600 dark:text-secondary-400">
                    <svg className="w-4 h-4 text-secondary-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Additional workflows now available */}
      <section className="py-12 bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <div className="rounded-xl bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 p-8">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300 text-xs font-semibold mb-4">
              Now available — 7 clinical note formats
            </div>
            <h3 className="font-semibold text-secondary-900 dark:text-white mb-2">
              Beyond IAR — session progress notes are live
            </h3>
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mb-4">
              In addition to IAR-DST documentation, Notematic now supports 6 progress note formats:
              SOAP, DAP, BIRP, GIRP, PIRP and PIE. Each follows the same clinician-reviewed,
              consent-gated workflow with full audit trail support.
            </p>
            <p className="text-sm font-medium text-primary-600 dark:text-primary-400">
              All workflows are included for existing subscribers at no additional charge.
            </p>
            <Link to="/workflows/clinical-notes" className="inline-block mt-4 text-sm text-primary-600 dark:text-primary-400 font-medium hover:underline">
              See all supported clinical note formats →
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
            See the IAR workflow in your service
          </h2>
          <p className="text-secondary-600 dark:text-secondary-400 mb-8">
            Book a demo to walk through the full IAR workflow and our 6 progress note formats,
            or request a guided pilot for your Medicare Mental Health Centre.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn btn-primary btn-lg">Book Demo</Link>
            <Link to="/contact" className="btn btn-outline btn-lg">Request Pilot</Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}

